import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Icon, InfoTip, Spinner } from '@/core/ui';
import { cx } from '@/core/utils';
import { useMap } from '@/features/map';
import { flyToItem } from '../flyToItem';
import { selectSearchItemAtom } from '../state';
import type { SearchViewport } from '../types';
import { useSearch } from '../useSearch';
import { SearchResults } from './SearchResults';

function SearchIcon() {
  return (
    <svg
      className="pointer-events-none absolute top-1/2 left-3 -mt-2.5 size-5 text-[#666]"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden
    >
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M15.5 15.5 21 21" />
    </svg>
  );
}

/** An example of each coordinate format, all near the same spot. */
const FORMAT_EXAMPLES = [
  ['dms', '40°26\'24"N 111°44\'47"W'],
  ['decimal', '40.4399, -111.7464'],
  ['utm', '12T 436700 4476950'],
  ['mgrs', '12T VK 36700 76950'],
  ['plusCode', '85GCC7XM+XC'],
] as const;

/** What can be typed in, behind an "i" button in the empty search box. */
function SearchHelp() {
  const { t } = useTranslation('search');
  return (
    // Its own negative margins keep the button flush with the box's right edge, as the clear button.
    <div className="absolute top-1/2 right-1 flex -translate-y-1/2">
      <InfoTip label={t('help')}>
        <p>{t('helpText')}</p>
        {/* Rows are laid out on their own, so each fits on one line; an example that still doesn't fit
            moves below its label whole rather than breaking. */}
        <dl className="mt-2 flex flex-col gap-1.5">
          {FORMAT_EXAMPLES.map(([format, example]) => (
            <div key={format} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
              <dt className="whitespace-nowrap text-hint">{t(`format.${format}`)}</dt>
              <dd>
                <code className="rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-xs whitespace-nowrap">
                  {example}
                </code>
              </dd>
            </div>
          ))}
        </dl>
      </InfoTip>
    </div>
  );
}

/** Place / coordinate search; picking a result flies there and drops a marker. */
export function SearchBox({ className }: { className?: string }) {
  const { t } = useTranslation('search');
  const map = useMap();
  const selectItem = useSetAtom(selectSearchItemAtom);

  const { inputProps, loading, list, highlight, select, clear } = useSearch({
    getViewport: (): SearchViewport => {
      const c = map?.getCenter() ?? { lat: 0, lng: 0 };
      return { lat: c.lat, lng: c.lng, zoom: map?.getZoom() ?? 0 };
    },
    onSelect: (item) => {
      if (map) flyToItem(map, item);
      selectItem(item);
    },
  });

  return (
    <div className={cx('relative', className)}>
      {loading ? <Spinner className="absolute top-1/2 left-3 -mt-2.5 size-5" /> : <SearchIcon />}
      <input
        {...inputProps}
        type="search"
        placeholder={t('placeholder')}
        aria-label={t('placeholder')}
        enterKeyHint="search"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
        // The browser's own clear button (WebKit) is replaced by ours, which is larger and looks the same everywhere.
        className="box-border h-12 w-full rounded-xl border border-neutral-300 bg-white pr-11 pl-10 [&::-webkit-search-cancel-button]:appearance-none text-base shadow-[0_2px_10px_rgba(0,0,0,.15)] focus:border-accent focus:shadow-[0_0_0_3px_rgba(44,123,182,.25)] focus:outline-none md:h-10 md:rounded-lg md:pr-9 md:pl-9 md:text-sm md:shadow-none"
      />
      {!inputProps.value && <SearchHelp />}
      {inputProps.value && (
        <button
          type="button"
          className="absolute top-1/2 right-1 flex size-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full text-[#666] hover:text-neutral-950 md:size-8"
          aria-label={t('clear')}
          // Keep focus in the input, so the suggestions don't close and reopen.
          onPointerDown={(e) => e.preventDefault()}
          onClick={clear}
        >
          <Icon className="size-[18px]">
            <path d="M6 6l12 12M18 6 6 18" />
          </Icon>
        </button>
      )}
      {list.open && (
        <SearchResults
          items={list.items}
          message={list.message}
          active={list.active}
          onHighlight={highlight}
          onSelect={select}
        />
      )}
    </div>
  );
}
